import { Component, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TierService } from '../../services/tier.service';
import { SystemAccessService } from '../../services/system-access.service';
import { MembershipService } from '../../services/membership.service';
import { PreferencesService } from '../../services/preferences.service';
import { DisplayDatePipe } from '../../pipes/display-date.pipe';

/** The longest delay a browser timer holds (2^31 - 1 ms, about 24.8 days); a longer one fires at once. */
const LONGEST_TIMER_MS = 2_147_483_647;

/**
 * Plan-ended banner. Shows when the brand's plan has lapsed (isReadOnly), telling members
 * their work is saved while editing is off, with a CTA to pick a plan. The sentence branches
 * on HOW the brand was entitled (trial / subscription / complimentary) — a lapsed comp must
 * not read "your subscription ended" — and comes in the reader's form: the admin form names
 * picking a plan in its third sentence for a member who may manage billing, the plain form
 * stops at "Your work is saved." for everyone else, beside the ask-an-admin line (Robert's
 * ruling of 2026-10-12, his English of 2026-10-13: the colour grades the same fact for every
 * reader, the words say only what the reader can do). An ENDED TRIAL that still holds images its bound left
 * untagged says so in its own second line, the trial banner's images sentence in the reader's
 * form: the one that names picking a plan for a member who may manage billing, the plain one
 * ("…once your brand picks a plan.") for everyone else (the plan state carries the count for the
 * ended trial as it did while it ran, and the trial banner is gone by then; Robert's rulings of
 * 2026-10-06). A further line says what happens to
 * the brand's public pages: they pause on a date (the server's one grace home, the instant the
 * plan state carries) and, from that instant, that they are paused. Members without the billing
 * permission see the one ask-an-admin sentence instead of an action they cannot complete. Reads
 * TierService, so it appears/clears live on plan changes. Cloned from demo-banner; CTA routes to
 * the billing settings tab.
 */
@Component({
  selector: 'app-subscription-banner',
  standalone: true,
  imports: [TranslateModule, RouterLink, DisplayDatePipe],
  templateUrl: './subscription-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './subscription-banner.component.scss'
})
export class SubscriptionBannerComponent {
  protected readonly tier = inject(TierService);
  protected readonly canManageBilling = signal(false);
  private readonly access = inject(SystemAccessService);
  private readonly membership = inject(MembershipService);
  private readonly preferences = inject(PreferencesService);
  private permissionResolved = false;

  /** The member's STORED zone: the pause date renders where the member lives, never the browser's. */
  protected readonly zone = computed(() => this.preferences.preferences().timeZone || 'UTC');

  /** The ended trial's images line: the trial's arm alone (the count is a trial's), while at least one image waits. */
  protected readonly showsDeferredImages = computed(() => this.tier.grantKind() === 'TRIAL' && this.tier.deferredImageCount() > 0);

  /** Moves when the timer armed for the pause instant fires, so the line below is judged again once the instant passes. */
  private readonly pauseClock = signal(0);

  /**
   * The public pages' line, by the instant the plan state carries (`publicFacesPauseAt`, stated while the brand is
   * lapsed and null while it is Active): before it the pages still show and the line names the date; from it they are
   * paused. Judged when the instant changes, at every landed plan-state read (a read that re-writes the same instant
   * notifies nobody, so the landing count is read beside it) and when the timer armed for the instant fires, so an
   * open tab turns to the paused line without a reload; no grace length lives in the app.
   */
  protected readonly pauseLine = computed<'pauseOn' | 'paused' | null>(() => {
    this.pauseClock();
    this.tier.loadCount();
    const at = this.tier.publicFacesPauseAt();
    if (!at) return null;
    return new Date(at).getTime() > Date.now() ? 'pauseOn' : 'paused';
  });

  constructor() {
    // Admitted members (and roots) only: this banner mounts at the app root on EVERY route,
    // including anonymous portal/share loads and a public brand's signed-in visitors with no
    // membership, and a non-member's access query would be refused and latch error state in the
    // shared permission service. The probe hangs on ADMISSION, never on the bare sign-in; a
    // monitoring root has no membership row and keeps the probe through the root axis. Brand
    // switches are full app boots (per-brand subdomain), so a one-time resolve after admission holds.
    effect(() => {
      if (!this.membership.isAdmittedOrRoot() || this.permissionResolved) {
        return;
      }
      this.permissionResolved = true;
      untracked(() => {
        this.access.hasPermission('MANAGE_BILLING').then(v => this.canManageBilling.set(v));
      });
    });

    // One timer per pause instant while it lies ahead, moving the clock the line reads. A wait longer than a browser
    // timer holds is cut to that cap and re-armed when it fires; a past, absent or unreadable instant arms nothing.
    effect(onCleanup => {
      this.pauseClock();
      const at = this.tier.publicFacesPauseAt();
      const wait = at ? new Date(at).getTime() - Date.now() : 0;
      if (!(wait > 0)) return;
      const timer = setTimeout(() => this.pauseClock.update(n => n + 1), Math.min(wait, LONGEST_TIMER_MS));
      onCleanup(() => clearTimeout(timer));
    });
  }
}
